/**
 * derive.ts — accent pre-selection, primary contrast guard, and the full
 * four-token derivation with hard per-token legibility guards (plan §3).
 *
 * The invariants callers rely on:
 *  - `contrastGuardPrimary` mirrors the buyer storefront `readableOn` fg pick
 *    (perceived luminance > 150 → `#1A1206` else `#FFFFFF`) and requires that
 *    fg to clear WCAG 4.5:1 against the primary.
 *  - `derivePalette` NEVER returns an ink/surface pair below 7:1 — each token
 *    falls back independently (`#F7F4EE` surface, `#0B1220` ink/accent) so one
 *    failure never blocks the others.
 */

import {
  contrastRatio,
  perceivedLuminance,
  deltaEHex,
  hexToOkLab,
  okLabToHex,
  okLabChroma,
  type OkLab,
} from "@a2z/ui/color/color-math";

export interface DerivedPalette {
  primary: string;
  accent: string;
  surface: string;
  ink: string;
}

// Buyer platform constants (must match apps/buyer StorefrontThemeStyle).
const DARK_INK = "#1A1206";
const WHITE = "#FFFFFF";
const READABLE_ON_THRESHOLD = 150;

// Guard thresholds.
const PRIMARY_MIN_CONTRAST = 4.5;
const ACCENT_MIN_CONTRAST = 4.5;
const INK_SURFACE_MIN_CONTRAST = 7;

// Per-token fallbacks (platform defaults).
const FALLBACK_SURFACE = "#F7F4EE";
const FALLBACK_INK = "#0B1220";
const FALLBACK_ACCENT = "#0B1220";

// Target OKLab lightness for the branded tints.
const SURFACE_TARGET_L = 0.97;
const SURFACE_MAX_CHROMA = 0.012;
const INK_TARGET_L = 0.2;
const INK_MAX_CHROMA = 0.03;

/**
 * The fg color the buyer would place on a solid button of `bg` — perceived
 * luminance > 150 → dark ink, else white. Exact mirror of buyer `readableOn`.
 */
export function readableFgOn(bg: string): string {
  return perceivedLuminance(bg) > READABLE_ON_THRESHOLD ? DARK_INK : WHITE;
}

/**
 * Mirrors buyer `readableOn`: true when the fg the buyer would pick for a solid
 * `primary` button clears WCAG 4.5:1. Colors near the luminance-150 midline
 * (muddy button text) fail here.
 */
export function contrastGuardPrimary(primaryHex: string): boolean {
  const fg = readableFgOn(primaryHex);
  return contrastRatio(primaryHex, fg) >= PRIMARY_MIN_CONTRAST;
}

/**
 * Pre-select the accent: most saturated mid-tone (OKLab L in ~0.35–0.70,
 * highest chroma), tie-broken by population/order. Advances past candidates
 * whose primary contrast guard fails; if none pass, returns the best-scoring
 * candidate anyway (seller can override). Empty palette → "".
 */
export function preselectAccent(palette: string[]): string {
  if (palette.length === 0) return "";

  const scored = palette
    .map((hex, idx) => {
      const ok = hexToOkLab(hex);
      const chroma = ok ? okLabChroma(ok) : 0;
      const L = ok ? ok.L : 0;
      // Reward mid-tone lightness and high chroma; idx as stable tiebreak.
      const midBonus = L >= 0.35 && L <= 0.7 ? 1 : 0.4;
      return { hex, idx, score: chroma * midBonus, guarded: contrastGuardPrimary(hex) };
    })
    // Highest score first; earlier palette index wins ties.
    .sort((a, b) => b.score - a.score || a.idx - b.idx);

  const passing = scored.find((c) => c.guarded);
  return (passing ?? scored[0]).hex;
}

/** Nudge an OKLab color toward a target lightness while capping chroma. */
function tint(sourceHex: string, targetL: number, maxChroma: number): string {
  const ok = hexToOkLab(sourceHex);
  if (!ok) return okLabToHex({ L: targetL, a: 0, b: 0 });
  const chroma = okLabChroma(ok);
  let a = ok.a;
  let b = ok.b;
  if (chroma > maxChroma && chroma > 0) {
    const f = maxChroma / chroma;
    a *= f;
    b *= f;
  }
  const tinted: OkLab = { L: targetL, a, b };
  return okLabToHex(tinted);
}

/** Darken a hex (reduce OKLab L by a step) — used to rescue a low-contrast accent. */
function darken(hex: string, step = 0.06): string {
  const ok = hexToOkLab(hex);
  if (!ok) return hex;
  return okLabToHex({ L: Math.max(0, ok.L - step), a: ok.a, b: ok.b });
}

/**
 * Derive the four storefront tokens from the chosen swatch + full palette,
 * under hard per-token guards. Order matters: surface and ink are computed
 * together and guarded as a pair against ≥7:1; accent is guarded ≥4.5:1 as
 * link text on the resolved surface.
 */
export function derivePalette(chosen: string, palette: string[]): DerivedPalette {
  const primary = chosen;

  // ── surface: near-white branded tint (primary hue, OKLab L≈0.97, low chroma).
  let surface = tint(primary, SURFACE_TARGET_L, SURFACE_MAX_CHROMA);

  // ── ink: near-black branded tint. Prefer the darkest palette survivor's hue,
  // else the primary's hue, at OKLab L≈0.20.
  const darkest = [...palette, primary]
    .filter((h) => hexToOkLab(h))
    .sort((a, b) => (hexToOkLab(a)!.L) - (hexToOkLab(b)!.L))[0] ?? primary;
  let ink = tint(darkest, INK_TARGET_L, INK_MAX_CHROMA);

  // ── ink/surface ≥7:1 guard, per-token fallback so one failure never blocks the other.
  if (contrastRatio(ink, surface) < INK_SURFACE_MIN_CONTRAST) {
    // First try the platform-safe surface with the branded ink.
    if (contrastRatio(ink, FALLBACK_SURFACE) >= INK_SURFACE_MIN_CONTRAST) {
      surface = FALLBACK_SURFACE;
    } else if (contrastRatio(FALLBACK_INK, surface) >= INK_SURFACE_MIN_CONTRAST) {
      ink = FALLBACK_INK;
    } else {
      surface = FALLBACK_SURFACE;
      ink = FALLBACK_INK;
    }
  }

  // ── accent: survivor with max ΔE from primary and decent chroma, guarded to
  // ≥4.5:1 as link text on the resolved surface; darken until it passes, else
  // fall back to darkened primary, else #0B1220.
  const accent = pickAccent(primary, palette, surface);

  return { primary, accent, surface, ink };
}

/** Choose + guard the accent against the resolved surface. */
function pickAccent(primary: string, palette: string[], surface: string): string {
  const candidates = palette
    .filter((h) => hexToOkLab(h))
    .map((hex) => ({
      hex,
      delta: deltaEHex(hex, primary),
      chroma: okLabChroma(hexToOkLab(hex)!),
    }))
    // Furthest from primary first, then more chromatic.
    .sort((a, b) => b.delta - a.delta || b.chroma - a.chroma);

  const guard = (hex: string) => contrastRatio(hex, surface) >= ACCENT_MIN_CONTRAST;

  for (const c of candidates) {
    if (guard(c.hex)) return c.hex;
    // Try darkening this candidate a few steps to rescue it.
    let cur = c.hex;
    for (let i = 0; i < 8; i++) {
      cur = darken(cur);
      if (guard(cur)) return cur;
    }
  }

  // Fallback: darkened primary.
  let cur = primary;
  for (let i = 0; i < 10; i++) {
    if (guard(cur)) return cur;
    cur = darken(cur);
  }
  if (guard(cur)) return cur;

  // Final fallback.
  return FALLBACK_ACCENT;
}
